import { memo } from 'react';
import Draggable from 'react-draggable';
import { TfiArrowCircleDown } from 'react-icons/tfi';

import { OButton } from '@src/shared';
import MagicPenSubMenu from '../MagicPenSubMenu/MagicPenSubMenu';

import useMagicPenTools from './useMagicPenTools';

import { DraggableIcon } from '@src/assets/icons';

import { COLOR_OPTIONS, DEFAULT_STICKERS, SHAPE_OPTIONS } from '../magicPen.constants';
import { TToolType } from '@src/types';

import styles from './magicPenTools.module.scss';

interface IMagicPenToolsProps {
  onClose: (toolbarPosition?: { x: number; y: number }) => void;
  buttonPosition?: { x: number; y: number };
}

const MagicPenTools = ({ onClose, buttonPosition }: IMagicPenToolsProps) => {
  const {
    nodeRef,
    activeTool,
    magicPenTools,
    isStickyNoteSelected,
    handleClose,
    handleDragStop,
    handleToolChange,
    handleColorSelect,
    handleShapeSelect,
    handleSelectSticker,
    setLastSelectedToolIcon,
    handleStickyNoteColorSelect,
    isToolVisible,
    shouldShowClearAllSubmenu,
    handleClearAll,
    handleClearMyAnnotations,
  } = useMagicPenTools({ onClose, buttonPosition });

  // Helper function to render a standard tool button
  const renderToolButton = (tool: string, icon: React.ReactNode, tooltip: string) => (
    <OButton
      isToolTipSmall
      toolTipContent={tooltip}
      toolTipPlacement="right"
      className={`${styles.button} ${activeTool === tool ? styles.active : ''}`}
      onClick={() => {
        handleToolChange(tool as TToolType);
        // Don't preserve icon for special tools
        if (!['clearAll', 'invite', 'save', 'colorPalette'].includes(tool)) {
          setLastSelectedToolIcon(icon);
        }
      }}
      icon={icon}
    />
  );

  return (
    <Draggable
      nodeRef={nodeRef}
      axis="x"
      bounds="parent"
      handle=".drag-handle"
      defaultPosition={buttonPosition ? { x: buttonPosition.x, y: 0 } : undefined}
      onStop={handleDragStop}
    >
      <div ref={nodeRef} className={styles.magicPenToolbar}>
        <div className={`${styles.draggableIcon} drag-handle`}>
          <DraggableIcon width="20px" height="20px" />
        </div>

        <div className={styles.toolsScrollArea}>
          {magicPenTools
            .filter(({ tool }) => isToolVisible(tool))
            .map(({ icon, tool, tooltip, isDividerBefore, isDividerAfter }) => (
              <div key={tooltip}>
                {isDividerBefore && <div className={styles.divider} />}
                {(() => {
                  switch (tool) {
                    case 'shapes':
                      return (
                        <MagicPenSubMenu icon={icon} key={tool} label="Lines & Shapes" tooltipContent="Shapes">
                          {SHAPE_OPTIONS.map(({ label, value, icon: shapeIcon }) => (
                            <button
                              key={value}
                              type="button"
                              className={styles.menuItem}
                              onClick={() => {
                                handleShapeSelect(value);
                                setLastSelectedToolIcon(shapeIcon);
                              }}
                            >
                              {shapeIcon}
                              <span>{label}</span>
                            </button>
                          ))}
                        </MagicPenSubMenu>
                      );

                    case 'sticker':
                      return (
                        <MagicPenSubMenu icon={icon} key={tool} label="Stickers" tooltipContent="Sticker">
                          <div className={styles.stickerGrid}>
                            {DEFAULT_STICKERS.map((emoji) => (
                              <button
                                key={emoji}
                                className={styles.stickerBtn}
                                onClick={() => {
                                  handleSelectSticker(emoji);
                                  setLastSelectedToolIcon(<span style={{ fontSize: '24px' }}>{emoji}</span>);
                                }}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </MagicPenSubMenu>
                      );

                    case 'colorPalette':
                      return (
                        <MagicPenSubMenu
                          icon={icon}
                          key={tool}
                          label="Color"
                          tooltipContent="Colors"
                          triggerId="magic-pen-color-palette-trigger"
                          popperId="magic-pen-color-palette-popup"
                        >
                          {COLOR_OPTIONS.map((color) => (
                            <button
                              key={color.label}
                              type="button"
                              className={styles.menuItem}
                              onPointerDown={(e) => {
                                // Prevent focus change to keep textarea text selection
                                e.preventDefault();
                              }}
                              onClick={
                                isStickyNoteSelected
                                  ? () => handleStickyNoteColorSelect(color.value)
                                  : () => handleColorSelect(color.value)
                              }
                            >
                              <div
                                className={styles.colorPaletteIcon}
                                style={{
                                  backgroundColor: color.value,
                                }}
                              />
                              <span>{color.label}</span>
                            </button>
                          ))}
                        </MagicPenSubMenu>
                      );

                    case 'clearAll':
                      // Show submenu only for HOST / COHOST acting host / Screen Sharer
                      return shouldShowClearAllSubmenu ? (
                        <MagicPenSubMenu
                          icon={icon}
                          key={tool}
                          label="Clear"
                          tooltipContent="Clear all"
                          triggerId="magic-pen-clear-all-trigger"
                          popperId="magic-pen-clear-all-popup"
                          offset={[3, 13]}
                        >
                          <button type="button" className={styles.menuItem} onClick={handleClearAll}>
                            <span>Clear All</span>
                          </button>
                          <button type="button" className={styles.menuItem} onClick={handleClearMyAnnotations}>
                            <span>Clear My Annotation Only</span>
                          </button>
                        </MagicPenSubMenu>
                      ) : (
                        renderToolButton(tool, icon, tooltip)
                      );

                    default:
                      return renderToolButton(tool, icon, tooltip);
                  }
                })()}
                {isDividerAfter && <div className={styles.divider} />}
              </div>
            ))}
        </div>
        <OButton
          toolTipContent="Close"
          toolTipPlacement="right"
          className={styles.closeButton}
          onClick={handleClose}
          icon={<TfiArrowCircleDown />}
        />
      </div>
    </Draggable>
  );
};

export default memo(MagicPenTools);
